import React, { useEffect, useRef, useState, useCallback } from 'react';

const jetId = process.env.REACT_APP_PAYCOMET_JET_ID || '';
const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://us-central1-fotocarnetmadrid.cloudfunctions.net';
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 120000;

function buildServerErrorMessage(data, response) {
  if (data && typeof data.error === 'string') {
    return data.error;
  }

  return `Error del servidor: ${response.status}`;
}

async function fetchPaycometPaymentStatus(order) {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/getPaycometPaymentStatus`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ order }),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok || !data || data.error) {
    throw new Error(buildServerErrorMessage(data, response));
  }

  return data;
}

const PaycometJetIframe = ({
  amount = '5.00',
  currency = 'EUR',
  onSuccess,
  onError,
}) => {
  const iframeRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const completionRef = useRef(false);
  const pollingRef = useRef({ timerId: null, token: 0, startedAt: 0, order: '' });
  const [error, setError] = useState(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [showRetry, setShowRetry] = useState(false);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onError, onSuccess]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current.timerId) {
      window.clearTimeout(pollingRef.current.timerId);
    }

    pollingRef.current = {
      timerId: null,
      token: pollingRef.current.token + 1,
      startedAt: 0,
      order: '',
    };
  }, []);

  const finalizeSuccess = useCallback(
    (data) => {
      if (completionRef.current) {
        return;
      }

      completionRef.current = true;
      stopPolling();
      setError(null);
      setShowRetry(false);

      if (onSuccessRef.current) {
        onSuccessRef.current(data);
      }
    },
    [stopPolling]
  );

  const finalizeError = useCallback(
    (message) => {
      stopPolling();
      setError(message);
      setShowRetry(true);

      if (onErrorRef.current) {
        onErrorRef.current(new Error(message));
      }
    },
    [stopPolling]
  );

  const startPollingOrder = useCallback(
    (order) => {
      const normalizedOrder = typeof order === 'string' ? order.trim() : '';
      if (!normalizedOrder || completionRef.current) {
        return;
      }

      stopPolling();
      setError(null);
      setShowRetry(false);

      const token = pollingRef.current.token + 1;
      pollingRef.current = {
        timerId: null,
        token,
        startedAt: Date.now(),
        order: normalizedOrder,
      };

      const poll = async () => {
        if (completionRef.current || pollingRef.current.token !== token) {
          return;
        }

        try {
          const result = await fetchPaycometPaymentStatus(normalizedOrder);
          if (completionRef.current || pollingRef.current.token !== token) {
            return;
          }

          if (result.status === 'completed') {
            finalizeSuccess({
              paymentMethod: 'PAYCOMET',
              amount,
              currency,
              status: 'completed',
              gatewayResponse: result,
            });
            return;
          }

          if (result.status === 'failed') {
            finalizeError(
              result.errorDescription || 'El pago no se ha completado.'
            );
            return;
          }
        } catch (_) {
          if (completionRef.current || pollingRef.current.token !== token) {
            return;
          }
        }

        if (Date.now() - pollingRef.current.startedAt >= POLL_TIMEOUT_MS) {
          setError(
            'No hemos podido confirmar el pago automaticamente. Si ya has terminado la verificacion, pulsa Reintentar.'
          );
          setShowRetry(true);
          return;
        }

        pollingRef.current.timerId = window.setTimeout(poll, POLL_INTERVAL_MS);
      };

      poll();
    },
    [amount, currency, finalizeError, finalizeSuccess, stopPolling]
  );

  const handleRetry = useCallback(() => {
    completionRef.current = false;
    stopPolling();
    setError(null);
    setShowRetry(false);
    setIframeReady(false);
    setIframeKey((k) => k + 1);
  }, [stopPolling]);

  const handleMessage = useCallback(
    (event) => {
      const msg = event.data;
      if (!msg || msg.source !== 'paycomet-embed') return;

      switch (msg.type) {
        case 'ready':
          setIframeReady(true);
          setError(null);
          setShowRetry(false);
          break;
        case 'success':
          finalizeSuccess(msg.data);
          break;
        case 'error':
          finalizeError(msg.data?.message || 'El pago no se ha completado.');
          break;
        case 'challenge':
          startPollingOrder(msg.data && msg.data.order);
          break;
        case 'scriptError':
          finalizeError('No se ha podido cargar el sistema de pago.');
          break;
        default:
          break;
      }
    },
    [finalizeError, finalizeSuccess, startPollingOrder]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      stopPolling();
    };
  }, [handleMessage, stopPolling]);

  useEffect(() => {
    if (iframeReady && iframeRef.current) {
      try {
        iframeRef.current.contentWindow.postMessage(
          { source: 'paycomet-parent', type: 'updateAmount', amount },
          '*'
        );
      } catch (_) {
        // iframe may have navigated to a different origin (3DS)
      }
    }
  }, [amount, iframeReady]);

  if (!jetId) {
    return (
      <div className="payment-inline-error">
        Falta configurar <code>REACT_APP_PAYCOMET_JET_ID</code> en el entorno.
      </div>
    );
  }

  const iframeSrc = `/payment-embed.html?amount=${encodeURIComponent(amount)}&currency=${encodeURIComponent(currency)}&jetid=${encodeURIComponent(jetId)}`;

  return (
    <div>
      <iframe
        key={iframeKey}
        ref={iframeRef}
        src={iframeSrc}
        title="Formulario de pago"
        style={{
          width: '100%',
          minHeight: 340,
          border: 'none',
          background: 'transparent',
          display: 'block',
        }}
        scrolling="no"
      />
      {error && (
        <div className="payment-inline-error">{error}</div>
      )}
      {showRetry && (
        <button
          type="button"
          className="payment-primary-button"
          onClick={handleRetry}
          style={{ marginTop: 12 }}
        >
          Reintentar pago
        </button>
      )}
    </div>
  );
};

export default PaycometJetIframe;
